import * as Yup from 'yup';

import {
  DATA_EXTRACTION_PROPERTY_TYPES,
  DATA_EXTRACTION_SCHEMA_MODES,
  type DataExtractionArrayItemType,
  type DataExtractionPropertyFormValue,
  type DataExtractionPropertyType,
  isEnumSupportedType,
  parseDataExtractionJsonSchema,
} from '@/lib/data-extraction';

/* eslint-disable @typescript-eslint/no-unused-vars */
declare module 'yup' {
  interface ArraySchema<TIn, TContext, TDefault, TFlags> {
    isSubsetOf(ref: Yup.Reference, message?: string): this;
  }
}
/* eslint-enable @typescript-eslint/no-unused-vars */

export const WEBHOOK_EVENTS = [
  { value: 'call_started', label: 'Call Started' },
  { value: 'call_ended', label: 'Call Ended' },
  { value: 'call_answered', label: 'Call Answered' },
  { value: 'call_transferred', label: 'Call Transferred' },
  { value: 'call_error', label: 'Call Error' },
] as const;

export type WebhookEvent = (typeof WEBHOOK_EVENTS)[number]['value'];

export const webhookConfig = Yup.object().shape({
  url: Yup.string().url('Invalid URL'),
  headers: Yup.array().of(
    Yup.object().shape({
      key: Yup.string().required('Header key is required'),
      value: Yup.string().required('Header value is required'),
    })
  ),
  query_params: Yup.array().of(
    Yup.object().shape({
      key: Yup.string().required('Query parameter key is required'),
      value: Yup.string().required('Query parameter value is required'),
    })
  ),
  timeout: Yup.number().min(0, 'Timeout must be at least 0'),
  events: Yup.array().of(Yup.string()),
});

Yup.addMethod(Yup.array, 'isSubsetOf', function (ref, message) {
  return this.test({
    name: 'isSubsetOf',
    exclusive: true,
    message: message || `Must be a subset of ${ref.path}`,
    params: { reference: ref.path },
    test: function (value) {
      // Resolve the actual value of the referenced field
      const masterList = this.resolve(ref) || [];

      // If the current field is empty/null, it is a valid subset
      if (!value || value.length === 0) return true;

      // Check if every item in the current array exists in the master array
      return value.every(item => masterList.includes(item));
    },
  });
});

const dataExtractionPropertySchema = Yup.object({
  id: Yup.string().required(),
  identifier: Yup.string().trim().required('Property name is required'),
  description: Yup.string().trim().default(''),
  dataType: Yup.mixed<DataExtractionPropertyType>()
    .oneOf([...DATA_EXTRACTION_PROPERTY_TYPES])
    .required('Property type is required'),
  required: Yup.boolean().default(false),
  arrayItemType: Yup.mixed<DataExtractionArrayItemType | null>()
    .nullable()
    .when('dataType', {
      is: 'array',
      then: schema =>
        schema
          .oneOf([...DATA_EXTRACTION_PROPERTY_TYPES.filter(type => type !== 'array'), null])
          .required('Array item type is required'),
      otherwise: schema => schema.nullable().default(null),
    }),
  enumValues: Yup.array()
    .of(Yup.string().trim())
    .default([])
    .test({
      name: 'valid-enum-values',
      message: 'Enum values must match the selected type',
      test(value) {
        const parent = this.parent as DataExtractionPropertyFormValue;
        const targetType = parent.dataType === 'array' ? parent.arrayItemType : parent.dataType;
        const enumValues = value?.map(item => item.trim()).filter(Boolean) ?? [];

        if (enumValues.length === 0 || !targetType || !isEnumSupportedType(targetType)) {
          return true;
        }

        return enumValues.every(item => {
          if (targetType === 'number') {
            return Number.isFinite(Number(item));
          }

          if (targetType === 'integer') {
            return /^-?\d+$/.test(item);
          }

          if (targetType === 'boolean') {
            return ['true', 'false'].includes(item.toLowerCase());
          }

          return true;
        });
      },
    }),
});

export const agentUpdateSchema = Yup.object().shape({
  agent_name: Yup.string().required('Agent name is required'),
  temperature: Yup.number().min(0).max(1).required('Temperature is required'),
  system_prompt: Yup.string()
    .trim()
    .min(1, 'System prompt is required')
    .required('System prompt is required'),
  prompt_variables: Yup.array().of(Yup.string()),

  voice_id: Yup.string().required('Voice ID is required'),

  ring_duration: Yup.number().min(0).required('Ring duration is required'),
  max_call_duration: Yup.number().min(0).required('Max call duration is required'),
  end_call_silence: Yup.number().min(0).required('End call silence is required'),
  is_call_recording_enabled: Yup.boolean().required(),
  webhook_config: webhookConfig.optional().nullable(),

  summary_prompt: Yup.string()
    .trim()
    .min(1, 'Summary prompt is required')
    .required('Summary prompt is required'),
  summary_prompt_variables: Yup.array()
    .of(Yup.string())
    .optional()
    .nullable()
    .isSubsetOf(
      Yup.ref('prompt_variables'),
      'Summary variables must match System prompt variables'
    ),

  data_extraction_prompt: Yup.string().trim().optional(),
  data_extraction_schema_mode: Yup.mixed<(typeof DATA_EXTRACTION_SCHEMA_MODES)[number]>()
    .oneOf([...DATA_EXTRACTION_SCHEMA_MODES])
    .default('builder'),
  data_extraction_json_schema: Yup.string()
    .default('')
    .test({
      name: 'valid-json-schema',
      message: 'Enter valid JSON schema',
      test(value) {
        const parent = this.parent as { data_extraction_schema_mode?: string };

        if (parent.data_extraction_schema_mode !== 'json') {
          return true;
        }

        return !parseDataExtractionJsonSchema(value).error;
      },
    }),
  data_extraction_root_is_array: Yup.boolean().default(false),
  data_extraction_prompt_variables: Yup.array()
    .of(Yup.string())
    .optional()
    .nullable()
    .isSubsetOf(
      Yup.ref('prompt_variables'),
      'Data extraction variables must match System prompt variables'
    ),
  data_extraction_properties: Yup.array()
    .of(dataExtractionPropertySchema)
    .default([])
    .test({
      name: 'unique-property-identifiers',
      message: 'Property names must be unique',
      test(value) {
        if (!value) {
          return true;
        }

        const identifiers = value.map(item => item.identifier.trim().toLowerCase()).filter(Boolean);

        return new Set(identifiers).size === identifiers.length;
      },
    }),

  sentiment_prompt: Yup.string()
    .trim()
    .min(1, 'Sentiment prompt is required')
    .required('Sentiment prompt is required'),
  sentiment_prompt_variables: Yup.array()
    .of(Yup.string())
    .optional()
    .nullable()
    .isSubsetOf(
      Yup.ref('prompt_variables'),
      'Sentiment variables must match System prompt variables'
    ),
  sentiment_tags: Yup.array()
    .of(
      Yup.object({
        id: Yup.string().required(),
        label: Yup.string().trim().required('Label is required'),
        description: Yup.string().trim().default(''),
      })
    )
    .min(1, 'Add at least one sentiment tag')
    .required('Add at least one sentiment tag')
    .test({
      name: 'unique-sentiment-labels',
      message: 'Sentiment tag labels must be unique',
      test(value) {
        if (!value) {
          return true;
        }

        const labels = value.map(item => item.label?.trim().toLowerCase()).filter(Boolean);

        return new Set(labels).size === labels.length;
      },
    }),
});

export type WebhookConfigFormValues = Yup.InferType<typeof webhookConfig>;

export type AgentUpdateFormValues = Yup.InferType<typeof agentUpdateSchema>;

export const agentCreateSchema = Yup.object().shape({
  agent_name: Yup.string().required('Agent name is required'),
  description: Yup.string().required('Description is required'),
  template_id: Yup.string().optional().nullable(),
  voice_id: Yup.string().required('Voice ID is required'),
});

export type AgentCreateFormValues = Yup.InferType<typeof agentCreateSchema>;
